import { HTMLAttributes, FC } from "react";
import clsx from "classnames";
import { useStyles } from "./MobileHeader.style";
import LogoIcon from "../../../assets/icons/logo_onmo+_1x.webp";
import LogoIcon2x from "../../../assets/icons/logo_onmo+_2x.webp";
import LogoIcon3x from "../../../assets/icons/logo_onmo+_3x.webp";
import GamePad from "../../../assets/console/onmo_controller_1x.webp";
import GamePad2x from "../../../assets/console/onmo_controller_2x.webp";
import GamePad3x from "../../../assets/console/onmo_controller_3x.webp";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { Image } from "../../core/Image/Image";
import { useLocation, useNavigate } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { Icon, useCustomNavigate } from "@onmo/ui";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { getStatusInfo } from "../../../models/user";
import { useAAASubscription } from "../../../hooks/useAAASubscription";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { useTranslation } from "react-i18next";
import { useCurrentProfile } from "../../../hooks/useProfileSession";

interface MobileHeaderProps extends HTMLAttributes<HTMLDivElement> {
  type: "main" | "sub";
  name?: string;
  onBack?: () => void;
}

export const MobileHeader: FC<MobileHeaderProps> = (props) => {
  const { className, type, name, onBack, ...otherProps } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { gamepads } = useGamepadContext();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: dataMe } = useAAASubscription({
    skip: !isAuthenticated,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { handleNavigateBack } = useCustomNavigate();
  const { currentProfile } = useCurrentProfile();

  return (
    <div {...otherProps} className={clsx(classes.root, className)}>
      <div className={classes.leftContent}>
        {type === "sub" ? (
          <Icon
            aria-label={t("A11y_Back")}
            name="icon-linear-arrow-left-3"
            size={24}
            onClick={onBack || handleNavigateBack}
            data-button-sound="back"
          />
        ) : location.pathname === PageRoutes.Home ? (
          <StaticImage
            src={LogoIcon}
            src2x={LogoIcon2x}
            src3x={LogoIcon3x}
            alt="ONMO+"
            className={classes.logo}
          />
        ) : (
          <Typography variant={TypographyVariant.HeadingH4Bold}>
            {name}
          </Typography>
        )}
      </div>
      {type === "sub" && name && (
        <div className={classes.centerContent}>
          <Typography variant={TypographyVariant.HeadingH6Bold}>
            {name}
          </Typography>
        </div>
      )}
      {type === "main" && (
        <div className={classes.rightContent}>
          {!!gamepads?.length && (
            <div className={classes.gamepad}>
              <div className={classes.totalGamepad}>{gamepads?.length}</div>
              <StaticImage
                alt=""
                src={GamePad}
                src2x={GamePad2x}
                src3x={GamePad3x}
                width={40}
              />
            </div>
          )}
          {dataMe?.me && (
            <div
              className={classes.avatar}
              role="button"
              tabIndex={-1}
              aria-label={t("A11y_Switch profile")}
              onClick={() =>
                navigate(PageRoutes.WhoIsPlaying, {
                  state: { from: location.pathname + location.search },
                })
              }
              data-gamepad="header-profile-avatar"
              data-selected-class={classes.selected}
              data-gamepad-no-auto-scroll="true"
              data-button-sound="selection"
            >
              <span className={classes.avatarImage}>
                <div
                  className={clsx(
                    classes.userStatus,
                    getStatusInfo(
                      dataMe?.me?.subscription?.status,
                      dataMe?.me?.subscription?.validUntil,
                      Boolean(dataMe?.me?.subscription?.isRecurring),
                    ),
                  )}
                />
                <Image
                  alt=""
                  src={currentProfile?.avatar}
                  width={40}
                  height={40}
                  radius={"50%"}
                />
              </span>
              <Icon name="icon-arrow-down" size={16} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
